import { Component, OnInit } from '@angular/core';
import { SharedService } from '../../services/shared.service';
import { Instructor } from '../../models/instructor.model';
import { InstructorService } from '../../services/instructor.service';
import { EstudianteService } from '../../services/estudiante.service';
import { Estudiante } from '../../models/estudiante.model';
import { CentroEntrenamiento } from '../../models/centro-entrenamiento.model';

@Component({
  selector: 'app-instructores',
  templateUrl: './instructores.component.html',
  styleUrls: ['./instructores.component.css']
})
export class InstructoresComponent implements OnInit {
  instructores: Instructor[] = [];
  filteredInstructores: Instructor[] = [];
  searchNombre: string = '';
  searchApellido: string = '';
  searchNroLibreta: number | null = null;
  activos: boolean = true;
  inactivos: boolean = true;
  initialInstructorState: { [key: number]: Partial<Instructor> } = {};
  isNroLibretaDuplicated: boolean = false;
  errorMessages: { [key: number]: string | null } = {};
  orderBy: string = 'graduacion'; // Changed from 'nroLibreta' to 'graduacion'
  orderDirection: string = 'desc'; // Changed from 'asc' to 'desc'
  showPopup: boolean = false;
  savedData: any = null;
  graduaciones: any[] = []; // Add this to store all graduations

  constructor(
    private sharedService: SharedService,
    private instructorService: InstructorService,
    private estudianteService: EstudianteService
  ) {}

  ngOnInit(): void {
    this.fetchGraduaciones().then(() => {
      this.subscribeInstructores();
    });
  }

  subscribeInstructores(): void {
    this.sharedService.instructores$.subscribe(
      (instructores) => {
        this.instructores = instructores.map(data => Instructor.fromRawData(data));
        
        // Fetch additional data for each instructor
        this.instructores.forEach(instructor => {
          instructor.fetchUltimoExamen(this.instructorService);
          instructor.fetchGraduacion(this.instructorService);
          instructor.fetchEmail(this.instructorService);
        });
        
        // Use setTimeout to ensure data has time to load before sorting
        setTimeout(() => {
          this.filterInstructores();
        }, 300);
      },
      (err) => {
        console.error('Error fetching instructores:', err);
      }
    );
  }

  resetInstructores(): void {    
    this.sharedService.fetchInstructores().subscribe(
      (instructores) => {
        this.instructores = instructores.map(data => Instructor.fromRawData(data));
        
        this.instructores.forEach(instructor => {
          instructor.fetchUltimoExamen(this.instructorService);
          instructor.fetchGraduacion(this.instructorService);
          instructor.fetchEmail(this.instructorService);
        });
        
        // Use setTimeout to ensure data has time to load before sorting
        setTimeout(() => {
          this.filterInstructores();
        }, 300);
      },
      (err) => {
        console.error('Error fetching instructores:', err);
      }
    );
  }

  // Update to return a Promise
  fetchGraduaciones(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.sharedService.fetchGraduaciones().subscribe(
        (graduaciones) => {
          this.graduaciones = graduaciones;
          resolve();
        },
        (err) => {
          console.error('Error fetching graduaciones:', err);
          resolve(); // Resolve even on error to continue initialization
        }
      );
    });
  }

  filterInstructores(): void {
    this.filteredInstructores = this.instructores.filter(instructor => {
      return (
        (this.searchNombre === '' || instructor.nombre?.toUpperCase().includes(this.searchNombre.toUpperCase())) &&
        (this.searchApellido === '' || instructor.apellido?.toUpperCase().includes(this.searchApellido.toUpperCase())) &&
        (this.searchNroLibreta === null || instructor.nroLibreta === this.searchNroLibreta) &&
        ((this.activos && instructor.activo) || (this.inactivos && !instructor.activo))
      );
    });

    this.filteredInstructores.sort((a, b) => {
      // Always place new instructors (id=0) at the top of the list regardless of sort
      if (a.id === 0) return -1;
      if (b.id === 0) return 1;
      
      let comparison = 0;
      if (this.orderBy === 'nroLibreta') {
        comparison = (a.nroLibreta || 0) - (b.nroLibreta || 0);
      } else if (this.orderBy === 'nombre') {
        comparison = (a.nombre || '').localeCompare(b.nombre || '');
      } else if (this.orderBy === 'apellido') {
        comparison = (a.apellido || '').localeCompare(b.apellido || '');
      } else if (this.orderBy === 'graduacion') {
        // Improved graduacion sorting using IDs
        const getGraduacionId = (instructor: Instructor) => {
          if (!instructor.graduacion) return -1; // No graduation = lowest rank
          
          // Find the graduation in our list by description and get its ID
          const grad = this.graduaciones.find(g => g.nombre === instructor.graduacion.nombre);
          return grad ? grad.id : -1;
        };
        
        const gradIdA = getGraduacionId(a);
        const gradIdB = getGraduacionId(b);
        comparison = gradIdA - gradIdB;
      }
      return this.orderDirection === 'asc' ? comparison : -comparison;
    });
  }

  clearFilters(): void {
    this.searchNombre = '';
    this.searchApellido = '';
    this.searchNroLibreta = null;
    this.activos = true;
    this.inactivos = true;
    this.orderBy = 'graduacion';
    this.orderDirection = 'desc';
    this.filterInstructores();
  }

  toggleEditMode(instructor: Instructor): void {
    if (instructor.isEditMode) {
      this.restoreInitialState(instructor);
    } else {
      this.saveInitialState(instructor);
      instructor.fetchUltimoExamen(this.instructorService);
      instructor.fetchGraduacion(this.instructorService);
      instructor.fetchEmail(this.instructorService);
    }
    instructor.isEditMode = !instructor.isEditMode;
  }

  saveInitialState(instructor: Instructor): void {
    this.initialInstructorState[instructor.id] = {
      id: instructor.id,
      nombre: instructor.nombre,
      apellido: instructor.apellido,
      nroLibreta: instructor.nroLibreta,
      activo: instructor.activo,
      disableValidation: instructor.disableValidation
    };
  }

  restoreInitialState(instructor: Instructor): void {
    const initialState = this.initialInstructorState[instructor.id];
    if (initialState) {
      Object.assign(instructor, initialState);
    }
  }

  addInstructor(): void {
    this.clearFilters();
    const newInstructor = new Instructor(0, "", "", null, true, true, true); // Initialize nroLibreta as null
    newInstructor.isEditMode = true;
    newInstructor.disableValidation = true;
    this.instructores.unshift(newInstructor);
    this.filterInstructores();
  }

  cancelAddInstructor(instructor: Instructor): void {
    this.instructores = this.instructores.filter(i => i !== instructor);
    this.filterInstructores();
  }

  afterSave(savedInstructor: Instructor): void {
    savedInstructor.isEditMode = false;
    const { id, isEditMode, ...dataToSave } = savedInstructor;
    this.savedData = dataToSave;
    this.showPopup = true;
    this.resetInstructores();
  }

  afterUpdate(updatedInstructor: Instructor): void {
    updatedInstructor.isEditMode = false;
    const { id, isEditMode, ...dataToSave } = updatedInstructor;
    this.savedData = dataToSave;
    this.showPopup = true;
    this.resetInstructores();
  }

  saveChanges(instructor: Instructor): void {
    instructor.disableValidation = false; // Enable validation before saving

    if (instructor.id === 0) {
      // New instructor
      this.instructorService.addInstructor(instructor).subscribe(
        (newInstructorData) => {
          this.afterSave(newInstructorData);
        },
        (err) => {
          console.error('Error adding instructor:', err);
        }
      );
    } else {
      // Existing instructor
      this.instructorService.updateInstructor(instructor).subscribe(
        (updatedInstructor) => {
          this.afterUpdate(updatedInstructor);
        },
        (err) => {
          console.error('Error updating instructor:', err);
        }
      );
    }
  }

  onNroLibretaChange(instructor: Instructor): void {
    if (instructor.nroLibreta !== null && instructor.nroLibreta !== 0) {
      const existingInstructor = this.instructores.find(i => i.nroLibreta === instructor.nroLibreta && i.id !== instructor.id);
      if (existingInstructor) {
        this.isNroLibretaDuplicated = true;
        this.errorMessages[instructor.id] = 'Numero de libreta ya registrado en otro instructor.';
        return;
      } else {
        this.isNroLibretaDuplicated = false;
      }

      this.estudianteService.getEstudiante(instructor.nroLibreta).subscribe(
        (estudiante: Estudiante) => {
          if(estudiante){
            instructor.nombre = estudiante.nombre;
            instructor.apellido = estudiante.apellido;
            instructor.disableValidation = false; // Enable validation after fetching estudiante
            this.errorMessages[instructor.id] = null; // Clear any previous error message
          }else{
            this.errorMessages[instructor.id] = 'Numero de libreta no registrado, puede ingresar un instructor con nro de libreta 0.';
          }
        },
        (err) => {
          console.error('Error fetching estudiante:', err);
          this.errorMessages[instructor.id] = 'Numero de libreta no registrado, puede ingresar un instructor con nro de libreta 0.';
        }
      );
    } else {
      this.errorMessages[instructor.id] = null;
    }
  }

  onNroLibretaKeyPress(event: KeyboardEvent, instructor: Instructor): void {
    if (event.key === 'Enter') {
      this.onNroLibretaChange(instructor);
    }
  }

  buscarEstudiante(instructor: Instructor): void {
    this.onNroLibretaChange(instructor);
  }

  guardar(instructor: Instructor): void {
    if (!instructor.nombre || !instructor.apellido) {
      this.errorMessages[instructor.id] = 'Nombre y Apellido son obligatorios.';
      return;
    } else {
      this.saveChanges(instructor);
    }
  }

  cancelar(instructor: Instructor): void {
    if (instructor.id === 0) {
      this.cancelAddInstructor(instructor);
    } else {
      this.toggleEditMode(instructor);
      this.errorMessages[instructor.id] = null;
    }
  }

  onActivosChange(): void {
    if (!this.activos && !this.inactivos) {
      this.inactivos = true; // Ensure at least one checkbox is selected
    }
    this.filterInstructores();
  }

  onInactivosChange(): void {
    if (!this.activos && !this.inactivos) {
      this.activos = true; // Ensure at least one checkbox is selected
    }
    this.filterInstructores();
  }

  closePopup(): void {
    this.showPopup = false;
    this.savedData = null;
  }
}
